import { useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';
import { usePagedList } from '../ui/use-paged-list';

export type OrganizationRow = components['schemas']['OrganizationRowResponse'];
export type OrganizationRowPage = components['schemas']['OrganizationRowPageResponse'];

/** Organizzazioni's filters, in the address: a search by name and the status (none is all). */
export type OrganizationFilters = {
  readonly q: string;
  readonly status: 'active' | 'archived' | null;
};

/**
 * Organizzazioni's rows (FR-AD-6): a tenant's organizations with their counts, archived ones
 * included unless the status narrows them, and the whole tenant's totals whatever the filters
 * («3 organizzazioni, 1 archiviata»). The shell's active organization travels on every call (the
 * client sets X-Active-Organization-Id), and its row comes back with `can.archive` false.
 */
export function useOrganizations(tenantId: string, { q, status }: OrganizationFilters) {
  const read = useCallback(
    (cursor: string | null) =>
      apiClient.GET('/v1/administration/tenants/{tenantId}/organization-overview', {
        params: {
          path: { tenantId },
          query: {
            ...(q !== '' ? { q } : {}),
            ...(status !== null ? { status } : {}),
            ...(cursor !== null ? { cursor } : {}),
          },
        },
      }),
    [tenantId, q, status],
  );
  return usePagedList<OrganizationRow, OrganizationRowPage>(read);
}
